import React, { useMemo, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { NewTrainingCourse } from "@/features/training/pages/training-tab/panel-left/actions/NewTrainingCourse";
import { NewTrainingFolder } from "@/features/training/pages/training-tab/panel-left/actions/NewTrainingFolder";
import { NewTrainingLink } from "@/features/training/pages/training-tab/panel-left/actions/NewTrainingLink";
import { CustomActionsButton } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

export default function TrainingActions() {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [folderModal, setFolderModal] = useState(false);
	const [linkModal, setLinkModal] = useState(false);
	const [courseModal, setCourseModal] = useState(false);

	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLElement>) =>
		setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openFolder = () => {
		setFolderModal(true);
		handleClose();
	};
	const openLink = () => {
		setLinkModal(true);
		handleClose();
	};
	const openCourse = () => {
		setCourseModal(true);
		handleClose();
	};

	// Compact menu styling (matches the other ones)
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary", // monochrome icons
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
		}),
		[],
	);

	return (
		<>
			<Tooltip
				title={
					<Translate String="Training.Actions.Tooltip">
						Create a new folder, link, or course
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "training-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="training-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "training-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openFolder}>
					<ListItemIcon>
						<CreateNewFolderIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Training.Actions.NewFolder">
								New Folder
							</Translate>
						}
					/>
				</MenuItem>

				<MenuItem sx={itemSx} onClick={openLink}>
					<ListItemIcon>
						<AddLinkIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Training.Actions.NewLink">
								New Link
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<MenuItem sx={itemSx} onClick={openCourse}>
					<ListItemIcon>
						<PlaylistAddIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate HTMLTag="span" String="Training.Actions.NewCourse">
								New Course
							</Translate>
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<NewTrainingFolder
				open={folderModal}
				handleClose={() => setFolderModal(false)}
			/>
			<NewTrainingLink
				open={linkModal}
				handleClose={() => setLinkModal(false)}
			/>
			<NewTrainingCourse
				open={courseModal}
				handleClose={() => setCourseModal(false)}
			/>
		</>
	);
}
